import { defineStore, acceptHMRUpdate } from 'pinia';

import { ProgressManagerEvent } from '~/lib/utils/ProgressManager';

import Worker from '~/worker/wWorker?worker';

import { mapRequestProfile } from '~/lib/utils/dataMapping';

import { deepToRaw } from '~/lib/utils/raw';

import {
  WorkerMessageOutType,
  WorkerMessageInType,
  WorkerMessageInInitValue,
} from '~/worker/SyncWorker';

import { PromisesQueueEventType } from '~/lib/utils/PromisesQueueCalls';

export const useSyncStore = defineStore('SyncStore', () => {
  const { contentSynchroFrequencyHour, userSynchroFrequencyHour } =
    useConfigStore();

  const profile = useProfileStore();

  const isInit = ref<boolean>(false);

  const loadProfile = async () => {
    const request = await useApiFetch(URI.Profile, {
      method: 'GET',
    });
    return mapRequestProfile(request.result);
  };

  const loadedEventStart = createEventHook<any>();
  const loadedEventProgress = createEventHook<any>();
  const loadedEventComplete = createEventHook<any>();
  const loadedEventCancel = createEventHook<any>();

  const context = useContextStore();

  const { getAdamAuthHeaders, getAdamAuthRefreshHeaders } = useAdamHeader();

  const runtimeConfig = useRuntimeConfig();

  const state = ref<{
    progress: number;
    current: number;
    total: number;
  }>({
    progress: 0,
    current: 0,
    total: -1,
  });

  const syncWorker = new Worker();

  const _init = async () => {
    const adamAuthHeaders = getAdamAuthHeaders();
    const adamAuthRefreshHeaders = getAdamAuthRefreshHeaders();
    return new Promise((resolve, reject) => {
      const handleMessage = (e) => {
        if (e.data) {
          switch (e.data.type) {
            case WorkerMessageOutType.InitComplete:
              resolve(true);
              syncWorker.removeEventListener('message', handleMessage);
              break;

            case WorkerMessageOutType.Error:
              reject(new Error('Error Init worker', e.data.value));
              syncWorker.removeEventListener('message', handleMessage);
              break;
          }
        }
      };
      syncWorker.addEventListener('message', handleMessage, false);
      syncWorker.postMessage({
        type: WorkerMessageInType.Init,
        value: new WorkerMessageInInitValue(
          runtimeConfig.public.apiBase,
          deepToRaw(adamAuthHeaders),
          deepToRaw(adamAuthRefreshHeaders),
          context.memberId,
          deepToRaw(profile.data)
        ),
      });
    });
  };

  const _load = async (loadedProfile: Profile, forced: boolean) => {
    return new Promise<{ tasksLength: number }>((resolve, reject) => {
      const handleMessage = (e) => {
        if (e.data) {
          switch (e.data.type) {
            case PromisesQueueEventType.CANCEL:
              break;
            case WorkerMessageOutType.LoadComplete:
              resolve(e.data.value);
              syncWorker.removeEventListener('message', handleMessage);
              break;

            case WorkerMessageOutType.Error:
              reject(new Error('Error Load worker', e.data.value));
              syncWorker.removeEventListener('message', handleMessage);
              break;
          }
        }
      };
      syncWorker.addEventListener('message', handleMessage, false);
      const value = {
        newProfile: deepToRaw(loadedProfile),
        contentSynchroFrequencyHour: forced ? 0 : contentSynchroFrequencyHour,
        userSynchroFrequencyHour: forced ? 0 : userSynchroFrequencyHour,
      };
      syncWorker.postMessage({
        type: WorkerMessageInType.Load,
        value,
      });
    });
  };

  const _cancel = async () => {
    const handleMessage = (e) => {
      if (e.data) {
        switch (e.data.type) {
          case PromisesQueueEventType.CANCEL:
            break;
          default:
            // do nothing
            break;
        }
      }
    };
    syncWorker.addEventListener('message', handleMessage, false);
    syncWorker.postMessage({ type: WorkerMessageInType.Cancel });
  };

  const loadAndRun = async (
    loadedProfile: Profile = null,
    forced: boolean = false,
    simultaneousPromises: number = 10
  ) => {
    if (!loadedProfile) {
      loadedProfile = await loadProfile();
    }
    if (!isInit.value) {
      await _init();
    } else {
      await _cancel();
    }

    isInit.value = true;

    await _load(loadedProfile, forced);

    await _run(simultaneousPromises);
  };

  const _run = async (simultaneousPromises: number = 10) => {
    const data = {
      simultaneousPromises,
    };

    return new Promise((resolve, reject) => {
      const handleMessage = (e) => {
        if (e.data) {
          switch (e.data.type) {
            case WorkerMessageOutType.SyncLoaded:
              break;
            case PromisesQueueEventType.START:
              state.value = e.data.value;
              loadedEventStart.trigger(e.data.value);
              break;
            case ProgressManagerEvent.Progress:
              state.value = e.data.value;
              loadedEventProgress.trigger(e.data.value);
              break;
            case WorkerMessageOutType.SyncComplete:
              loadedEventComplete.trigger(e.data.value);
              resolve(e.data);
              syncWorker.removeEventListener('message', handleMessage);

              break;
            case PromisesQueueEventType.CANCEL:
              resolve(null);
              loadedEventCancel.trigger();
              syncWorker.removeEventListener('message', handleMessage);
              break;
            case WorkerMessageOutType.Error:
              reject(new Error('Error worker', e.data.value));
              syncWorker.removeEventListener('message', handleMessage);
              break;
          }
        }
      };
      syncWorker.addEventListener('message', handleMessage, false);
      syncWorker.postMessage({ type: WorkerMessageInType.Sync, value: data });
    });
  };

  return {
    loadedEventStart,
    loadedEventProgress,
    loadedEventComplete,
    loadedEventCancel,
    loadAndRun,
    state,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSyncStore, import.meta.hot));
}
